import { Button, Modal } from "@legal-500/legal500-design-system";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { cms } from "../../../../api/cms.service";
import type { ApiError } from "../../../../api/client";
import type { CmsPagesExportFile, CmsPagesImportPreview } from "../../../../types/cms";
import {
  countImportChanges,
  hasBlockedChildPages,
  hasBlockedPages,
  hasBlockedSectionHome,
  defaultImportSidebar,
  type ImportSidebarSelection,
  NO_IMPORT_SIDEBAR,
  toImportedPages,
  toImportSidebar,
} from "../../pagesTransfer/pagesImport";
import {
  imageName,
  imagesToUpload,
  storedImageName,
  toImageFile,
  withoutImageData,
} from "../../pagesTransfer/importImages";
import type { ImportedPages, PageLinksLookup } from "../../pagesTransfer/pagesTransferTypes";
import { parsePagesExportFile, readFileText } from "../../pagesTransfer/parsePagesExportFile";
import { pagePath } from "../../pageAddress";
import { ImportPreview } from "./ImportPreview";
import { ImportResult } from "./ImportResult";
import styles from "./PagesImportModal.module.css";

type PagesImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
};

async function lookUpPageLinks(): Promise<PageLinksLookup> {
  try {
    const { pages } = await cms.getPages();
    return {
      status: "found",
      pageIdsByPath: new Map(pages.map(page => [pagePath(page), page.id])),
    };
  } catch (err) {
    console.error("[PagesImportModal] failed to look up the imported pages' links", err);
    return { status: "failed" };
  }
}

type ApiErrorIssue = NonNullable<ApiError["details"]>[number];

const ISSUES_SHOWN = 2;

function describeIssue(issue: ApiErrorIssue): string {
  if (issue.path.length === 0) {
    return issue.message;
  }
  const location = issue.path
    .map(part => (typeof part === "number" ? String(part + 1) : part))
    .join(" › ");
  return `${issue.message} (at ${location})`;
}

function withReason(message: string, err: unknown): string {
  if (!(err instanceof Error)) {
    return message;
  }
  const issues = ((err as ApiError).details ?? []).slice(0, ISSUES_SHOWN);
  if (issues.length === 0) {
    return `${message} ${err.message}`;
  }
  return `${message} ${err.message}: ${issues.map(describeIssue).join("; ")}`;
}

export function PagesImportModal({ isOpen, onClose, onImported }: PagesImportModalProps) {
  const [exportFile, setExportFile] = useState<CmsPagesExportFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [checkingFile, setCheckingFile] = useState(false);
  const [preview, setPreview] = useState<CmsPagesImportPreview | null>(null);
  const [overwriteKeys, setOverwriteKeys] = useState<string[]>([]);
  const [importSidebar, setImportSidebar] = useState<ImportSidebarSelection>(NO_IMPORT_SIDEBAR);
  const [importing, setImporting] = useState(false);
  const [importSent, setImportSent] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedPages, setImportedPages] = useState<ImportedPages | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const uploadedImages = useRef(new Set<string>());
  const flowId = useRef(0);

  const resetFlow = useCallback(() => {
    flowId.current += 1;
    setExportFile(null);
    setFileError(null);
    setCheckingFile(false);
    setPreview(null);
    setOverwriteKeys([]);
    setImportSidebar(NO_IMPORT_SIDEBAR);
    setImporting(false);
    setImportSent(false);
    setImportError(null);
    setImportedPages(null);
    setUploadProgress(null);
    uploadedImages.current = new Set();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetFlow();
    }
  }, [isOpen, resetFlow]);

  function close() {
    if (importSent) {
      return;
    }

    resetFlow();
    onClose();
  }

  async function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    resetFlow();
    const checkId = flowId.current;
    setCheckingFile(true);
    try {
      const parsed = parsePagesExportFile(await readFileText(file));
      if (checkId !== flowId.current) {
        return;
      }
      if (!parsed.exportFile) {
        setFileError(parsed.error);
        return;
      }
      const filePreview = await cms.previewPagesImport(withoutImageData(parsed.exportFile));
      if (checkId !== flowId.current) {
        return;
      }
      setExportFile(parsed.exportFile);
      setPreview(filePreview);
      setImportSidebar(defaultImportSidebar(filePreview.sidebar));
    } catch (err) {
      if (checkId === flowId.current) {
        setFileError(withReason("The file couldn't be checked.", err));
      }
    } finally {
      if (checkId === flowId.current) {
        setCheckingFile(false);
      }
    }
  }

  function changeOverwrite(key: string, overwrite: boolean) {
    setOverwriteKeys(previous => {
      const others = previous.filter(existing => existing !== key);
      return overwrite ? [...others, key] : others;
    });
  }

  async function uploadMissingImages(
    file: CmsPagesExportFile,
    filePreview: CmsPagesImportPreview,
    importId: number,
  ): Promise<string | null> {
    const pending = imagesToUpload(filePreview, file, uploadedImages.current);
    for (const [index, image] of pending.entries()) {
      setUploadProgress({ current: index + 1, total: pending.length });
      const name = imageName(image);
      let stored: { hash: string; url: string };
      try {
        stored = await cms.uploadImage(toImageFile(image));
      } catch (err) {
        return withReason(`The image ${name} couldn't be uploaded, so nothing was imported.`, err);
      }
      if (importId !== flowId.current) {
        return null;
      }
      const storedName = storedImageName(stored);
      if (storedName !== name) {
        return `The image ${name} was stored as ${storedName}, which doesn't match the file, so nothing was imported.`;
      }
      uploadedImages.current.add(name);
    }
    return null;
  }

  async function runImport() {
    if (!exportFile || !preview) {
      return;
    }

    const sidebar = preview.sidebar ? toImportSidebar(importSidebar) : undefined;
    if (sidebar === null) {
      setImportError("Choose which sidebar to use.");
      return;
    }

    const importId = flowId.current;
    setImporting(true);
    setImportError(null);
    try {
      const uploadError = await uploadMissingImages(exportFile, preview, importId);
      if (importId !== flowId.current) {
        return;
      }
      setUploadProgress(null);
      if (uploadError !== null) {
        setImportError(uploadError);
        return;
      }
      setImportSent(true);
      const result = await cms.importPages({
        file: withoutImageData(exportFile),
        overwrite: overwriteKeys,
        sidebar,
      });
      onImported();
      const pageLinks = await lookUpPageLinks();
      if (importId === flowId.current) {
        setImportedPages(toImportedPages(result, preview, pageLinks, exportFile));
      }
    } catch (err) {
      if (importId === flowId.current) {
        setImportError(withReason("The pages couldn't be imported.", err));
      }
    } finally {
      if (importId === flowId.current) {
        setImporting(false);
        setImportSent(false);
        setUploadProgress(null);
      }
    }
  }

  const changes = preview ? countImportChanges(preview, overwriteKeys) : null;
  const nothingToImport = changes !== null && changes.created + changes.overwritten === 0;
  const blockedPages = preview !== null && hasBlockedPages(preview);

  return (
    <Modal isOpen={isOpen} onClose={close} title="Import pages" size="large">
      <div className={styles.flow}>
        {importedPages && (
          <>
            <ImportResult importedPages={importedPages} />

            <div className={styles.actions}>
              <Button type="button" variant="contained" color="primary" onClick={close}>
                Done
              </Button>
            </div>
          </>
        )}

        {!importedPages && preview && exportFile && (
          <>
            <ImportPreview
              preview={preview}
              exportFile={exportFile}
              overwriteKeys={overwriteKeys}
              onOverwriteChange={changeOverwrite}
              sidebarSelection={importSidebar}
              onSidebarChange={setImportSidebar}
            />

            {hasBlockedChildPages(preview) && (
              <p className={styles.error} role="alert">
                Some pages can't be imported yet because their parent page isn't on this site.
                Import the parent pages first.
              </p>
            )}

            {hasBlockedSectionHome(preview) && (
              <p className={styles.error} role="alert">
                The file's Section home can't be imported because this site already has a different
                home for that section.
              </p>
            )}

            {nothingToImport && (
              <p className={styles.hint}>
                Nothing to import: every page is kept as it is. Choose Overwrite to replace a page.
              </p>
            )}

            {uploadProgress && (
              <p className={styles.hint} role="status">
                Uploading images: {uploadProgress.current} of {uploadProgress.total}
              </p>
            )}

            {importError && (
              <p className={styles.error} role="alert">
                {importError}
              </p>
            )}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="outlined"
                color="default"
                onClick={resetFlow}
                disabled={importing}
              >
                Choose another file
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={runImport}
                disabled={importing || nothingToImport || blockedPages}
              >
                {uploadProgress ? "Uploading images…" : importing ? "Importing…" : "Import pages"}
              </Button>
            </div>
          </>
        )}

        {!importedPages && !preview && (
          <>
            <p className={styles.hint}>
              Choose a file exported from another environment. You'll see what will change before
              anything is imported.
            </p>

            <div className={styles.fileField}>
              <label className={styles.fileLabel} htmlFor="pages-import-file">
                Export file (.json)
              </label>
              <input
                id="pages-import-file"
                type="file"
                accept=".json,application/json"
                onChange={chooseFile}
                disabled={checkingFile}
                aria-describedby={fileError ? "pages-import-file-error" : undefined}
              />
            </div>

            {checkingFile && <p className={styles.hint}>Checking the file…</p>}

            {fileError && (
              <p id="pages-import-file-error" className={styles.error} role="alert">
                {fileError}
              </p>
            )}

            <div className={styles.actions}>
              <Button type="button" variant="outlined" color="default" onClick={close}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
